import { API_BASE_URL } from '@synergy/config';
import { Box, Button, Stack } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloRecordCard } from '@synergy/components-palo';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import urlJoin from 'url-join';

import { OrderApproval } from '../models/order-approval';
import { PaloApprovalItems } from './palo-approval-items';

const Fact = ({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement => (
    <Stack sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>{label}</Box>
        <Box sx={{ fontSize: '12.5px' }}>{children || '–'}</Box>
    </Stack>
);

const daysSince = (date?: string): number =>
    date ? Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000)) : 0;

export interface PaloApprovalCardProps {
    approval: OrderApproval;
    /** Only the shop admin decides; everyone else sees their own order wait. */
    canDecide: boolean;
    onApprove: () => void;
    onDecline: () => void;
}

/**
 * One order waiting for approval, with what the approver needs to decide on
 * it: who ordered when, where it goes, the amount against the orderer's
 * limit, and on opening the positions (#2281).
 */
export const PaloApprovalCard = ({
    approval,
    canDecide,
    onApprove,
    onDecline,
}: PaloApprovalCardProps): ReactElement => {
    const [open, setOpen] = useState(true);
    const currency = approval.totalPrice.currency;
    const money = (value: number): ReactNode => <FormattedNumber value={value} style="currency" currency={currency} />;
    const perOrder = approval.perOrderOrderLimit ?? undefined;
    const overages = [
        { id: 'app.orderApprovals.over', amount: approval.perOrderOverage },
        { id: 'app.orderApprovals.overMonthly', amount: approval.monthlyOverage },
    ];

    return (
        <PaloRecordCard
            open={open}
            onToggle={() => setOpen(current => !current)}
            title={
                <FormattedMessage
                    id="app.orderApprovals.card.title"
                    values={{ number: approval.number, orderer: approval.customerContact?.fullName ?? '–' }}
                />
            }
            meta={
                <FormattedMessage
                    id="app.orderApprovals.card.meta"
                    values={{
                        days: daysSince(approval.orderDate),
                        suppliers: approval.supplierCount,
                        items: approval.itemCount,
                        amount: money(approval.totalNetPrice),
                    }}
                />
            }
            actions={
                <Stack direction="row" gap="6px" onClick={event => event.stopPropagation()}>
                    <Button
                        size="small"
                        variant="text"
                        href={urlJoin(API_BASE_URL, `/cart/${approval.id}/pdf`)}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FormattedMessage id="app.orderApprovals.pdf" />
                    </Button>
                    {canDecide && (
                        <>
                            <Button size="small" variant="outlined" color="error" onClick={onDecline}>
                                <FormattedMessage id="app.orderApprovals.decline" />
                            </Button>
                            <Button size="small" variant="contained" onClick={onApprove}>
                                <FormattedMessage id="app.orderApprovals.approve" />
                            </Button>
                        </>
                    )}
                </Stack>
            }
        >
            <Stack gap="12px">
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '10px 20px',
                    }}
                >
                    {approval.orderReference && (
                        <Fact label={<FormattedMessage id="app.orders.detail.orderReference" />}>
                            {approval.orderReference}
                        </Fact>
                    )}
                    <Fact label={<FormattedMessage id="app.orderApprovals.orderedAt" />}>
                        {approval.orderDate && (
                            <FormattedDate
                                value={approval.orderDate}
                                year="numeric"
                                month="2-digit"
                                day="2-digit"
                                hour="2-digit"
                                minute="2-digit"
                            />
                        )}
                    </Fact>
                    <Fact label={<FormattedMessage id="app.orders.column.deliveryAddress" />}>
                        {[approval.deliveryAddress?.title, approval.deliveryAddress?.city].filter(Boolean).join(', ')}
                    </Fact>
                    <Fact label={<FormattedMessage id="app.orders.column.costCentre" />}>{approval.costCentre}</Fact>
                    <Fact label={<FormattedMessage id="app.orderApprovals.limits" />}>
                        {undefined !== perOrder || approval.monthlyOrderLimit ? (
                            <FormattedMessage
                                id="app.orderApprovals.limits.value"
                                values={{
                                    perOrder: undefined !== perOrder ? money(perOrder) : '–',
                                    monthly: approval.monthlyOrderLimit ? money(approval.monthlyOrderLimit) : '–',
                                }}
                            />
                        ) : null}
                    </Fact>
                    {/* Each limit the cart goes past, as the mail to the admins names it. */}
                    {overages.map(({ id, amount }) =>
                        amount ? (
                            <Fact key={id} label={<FormattedMessage id={id} />}>
                                <Box component="span" sx={{ color: tokens.color.crit, fontWeight: 600 }}>
                                    {money(amount)}
                                </Box>
                            </Fact>
                        ) : null
                    )}
                </Box>
                {open && <PaloApprovalItems id={approval.id} currency={currency} />}
            </Stack>
        </PaloRecordCard>
    );
};
